import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { userSearchApi, workTasksApi } from "@/api/workTasks";
import type {
  CreateWorkTaskDto,
  TaskTagCreateDto,
  TaskTagUpdateDto,
  UpdateWorkTaskDto,
  WorkTaskListQuery,
  WorkTaskRelatedEntityType,
  WorkTaskStatus,
} from "@/api/workTasks";

/**
 * Tasks Module v1.0 — React Query hooks. All mutations invalidate the
 * "tasks" query key family so list pages, detail pages, and the
 * dashboard widget stay in sync after any change.
 */

export const taskKeys = {
  all: ["tasks"] as const,
  list: (query?: WorkTaskListQuery) => ["tasks", "list", query] as const,
  detail: (id: string) => ["tasks", "detail", id] as const,
  comments: (id: string) => ["tasks", id, "comments"] as const,
  activity: (id: string) => ["tasks", id, "activity"] as const,
  myOpen: (limit: number) => ["tasks", "my-open", limit] as const,
  byRelated: (type: WorkTaskRelatedEntityType, entityId: string) =>
    ["tasks", "by-related", type, entityId] as const,
  // Deliberately nested UNDER byRelated: every task mutation invalidates
  // `taskKeys.all`, and any future narrower invalidation of `byRelated`
  // prefix-matches this too, so a Tasks-tab badge cannot go stale while the
  // list beside it refreshes.
  openCountByRelated: (type: WorkTaskRelatedEntityType, entityId: string) =>
    ["tasks", "by-related", type, entityId, "open-count"] as const,
  tags: ["task-tags"] as const,
  adminTags: ["task-tags", "admin"] as const,
};

export function useWorkTasks(query: WorkTaskListQuery) {
  return useQuery({
    queryKey: taskKeys.list(query),
    queryFn: () => workTasksApi.list(query),
  });
}

export function useWorkTask(id: string | undefined) {
  return useQuery({
    queryKey: id ? taskKeys.detail(id) : ["tasks", "detail", "missing"],
    queryFn: () => workTasksApi.get(id!),
    enabled: Boolean(id),
  });
}

export function useCreateWorkTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateWorkTaskDto) => workTasksApi.create(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

export function useUpdateWorkTask(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateWorkTaskDto) => workTasksApi.update(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

export function useChangeWorkTaskStatus(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (status: WorkTaskStatus) => workTasksApi.changeStatus(id, status),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

export function useReassignWorkTask(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assigneeId: string | null) => workTasksApi.reassign(id, assigneeId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

export function useSetWorkTaskProgress(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (percent: number) => workTasksApi.setProgress(id, percent),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

export function useSetWorkTaskParticipants(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (participantIds: string[]) => workTasksApi.setParticipants(id, participantIds),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

export function useDeleteWorkTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => workTasksApi.delete(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

export function useRestoreWorkTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => workTasksApi.restore(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

export function useTaskComments(taskId: string | undefined) {
  return useQuery({
    queryKey: taskId ? taskKeys.comments(taskId) : ["tasks", "comments", "missing"],
    queryFn: () => workTasksApi.listComments(taskId!),
    enabled: Boolean(taskId),
  });
}

export function useAddTaskComment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content: string) => workTasksApi.addComment(taskId, content),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taskKeys.comments(taskId) });
      void qc.invalidateQueries({ queryKey: taskKeys.activity(taskId) });
    },
  });
}

export function useUpdateTaskComment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { commentId: string; content: string }) =>
      workTasksApi.updateComment(taskId, vars.commentId, vars.content),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taskKeys.comments(taskId) });
      void qc.invalidateQueries({ queryKey: taskKeys.activity(taskId) });
    },
  });
}

export function useDeleteTaskComment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => workTasksApi.deleteComment(taskId, commentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taskKeys.comments(taskId) });
      void qc.invalidateQueries({ queryKey: taskKeys.activity(taskId) });
    },
  });
}

export function useTaskActivity(id: string | undefined, skip = 0, take = 50) {
  return useQuery({
    queryKey: id ? [...taskKeys.activity(id), skip, take] : ["tasks", "activity", "missing"],
    queryFn: () => workTasksApi.getActivity(id!, skip, take),
    enabled: Boolean(id),
  });
}

export function useTaskTags() {
  return useQuery({
    queryKey: taskKeys.tags,
    queryFn: () => workTasksApi.listTags(),
    // Lookup data — caches for five minutes.
    staleTime: 5 * 60 * 1000,
  });
}

export function useAdminTaskTags() {
  return useQuery({
    queryKey: taskKeys.adminTags,
    queryFn: () => workTasksApi.listAdminTags(),
    staleTime: 60 * 1000,
  });
}

export function useCreateTaskTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: TaskTagCreateDto) => workTasksApi.createTag(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taskKeys.tags });
      void qc.invalidateQueries({ queryKey: taskKeys.adminTags });
    },
  });
}

export function useUpdateTaskTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; payload: TaskTagUpdateDto }) =>
      workTasksApi.updateTag(vars.id, vars.payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taskKeys.tags });
      void qc.invalidateQueries({ queryKey: taskKeys.adminTags });
    },
  });
}

export function useDeactivateTaskTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => workTasksApi.deactivateTag(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taskKeys.tags });
      void qc.invalidateQueries({ queryKey: taskKeys.adminTags });
    },
  });
}

export function useMyOpenTasks(limit = 5) {
  return useQuery({
    queryKey: taskKeys.myOpen(limit),
    queryFn: () => workTasksApi.myOpen(limit),
  });
}

/**
 * Full task list for an entity. Use this ONLY where the rows are rendered
 * (TaskListMini). A detail page that just needs the Tasks tab badge should
 * call {@link useOpenTaskCountByRelated} — fetching the list to count it in
 * the browser costs the whole payload on every page load.
 */
export function useTasksByRelated(
  type: WorkTaskRelatedEntityType,
  entityId: string | undefined,
) {
  return useQuery({
    queryKey: entityId
      ? taskKeys.byRelated(type, entityId)
      : ["tasks", "by-related", type, "missing"],
    queryFn: () => workTasksApi.listByRelated(type, entityId!),
    enabled: Boolean(entityId) && type !== "None",
  });
}

/**
 * Open-task count (status != Done) for an entity's Tasks tab badge, counted
 * server-side over the same visibility scope the list read uses. `data` is
 * `undefined` until it resolves, so callers render `data ?? 0` — matching the
 * old client-side `(list ?? []).filter(...).length`, which was also 0 while
 * the list was in flight.
 */
export function useOpenTaskCountByRelated(
  type: WorkTaskRelatedEntityType,
  entityId: string | undefined,
) {
  return useQuery({
    queryKey: entityId
      ? taskKeys.openCountByRelated(type, entityId)
      : ["tasks", "by-related", type, "missing", "open-count"],
    queryFn: () => workTasksApi.countOpenByRelated(type, entityId!),
    enabled: Boolean(entityId) && type !== "None",
  });
}

/**
 * PR 3 — user lookup for the @mention autocomplete dropdown. Caller
 * controls the trigger (typed-> debounced query) and a `null` query
 * disables the request so an empty input does not flood the endpoint.
 */
export function useUserSearch(query: string | null, limit = 10) {
  return useQuery({
    queryKey: ["users", "search", query ?? "", limit],
    queryFn: () => userSearchApi.search(query ?? "", limit),
    enabled: query !== null,
    staleTime: 30_000,
  });
}
